import { useState, useCallback } from 'react';
import { Asset, launchCamera } from 'react-native-image-picker';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useQueryClient } from '@tanstack/react-query';
import { api } from 'app/api/client';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
import { TRootStackParamList } from 'app/types/navigation';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { requestCameraPermission } from '@shared/utils/cameraPermission';

type NavigationProp = NativeStackNavigationProp<TRootStackParamList>;

type UseDocumentUploadOptions = {
  onBeforeCameraLaunch?: () => void;
  onAfterCameraLaunch?: () => void;
  reviewBeforeUpload?: boolean;
};

export const useDocumentUpload = (pathwayId: string, options?: UseDocumentUploadOptions) => {
  const navigation = useNavigation<NavigationProp>();
  const queryClient = useQueryClient();
  const { t } = dynamicTranslations();
  const { onBeforeCameraLaunch, onAfterCameraLaunch, reviewBeforeUpload = false } = options ?? {};

  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState<Asset | null>(null);

  const uploadPhoto = useCallback(async (asset: Asset): Promise<boolean> => {
    const formData = new FormData();
    formData.append('files[0]', {
      uri: asset.uri,
      name: asset.fileName ?? 'document.jpg',
      type: asset.type ?? 'image/jpeg',
    } as any);
    formData.append('origin', 'patient');
    formData.append('patient_access', 'true');

    try {
      setIsUploading(true);
      await api.post(`rpm/api/v1/pathways/${pathwayId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      queryClient.invalidateQueries({ queryKey: [['pathway-documents', pathwayId]] });
      ToastNotifications.displaySuccessNotification(
        t('history.documents.patientSection.toast.uploadSuccess'),
      );
      return true;
    } catch {
      ToastNotifications.displayErrorNotification(
        t('history.documents.patientSection.toast.uploadError'),
      );
      return false;
    } finally {
      setIsUploading(false);
    }
  }, [pathwayId, queryClient, t]);

  const handleCameraUpload = useCallback(async () => {
    setIsUploadModalVisible(false);

    await new Promise<void>((resolve) => setTimeout(resolve, 350));

    const hasPermission = await requestCameraPermission(
      t('history.documents.patientSection.camera.permissionTitle'),
      t('history.documents.patientSection.camera.permissionMessage'),
      t('history.documents.patientSection.camera.permissionCancel'),
      t('history.documents.patientSection.camera.permissionSettings'),
    );
    if (!hasPermission) return;

    onBeforeCameraLaunch?.();
    await new Promise<void>((resolve) => setTimeout(resolve, 300));

    let result;
    try {
      result = await launchCamera({ mediaType: 'photo', quality: 0.8 });
    } catch {
      ToastNotifications.displayErrorNotification(
        t('history.documents.patientSection.toast.uploadError'),
      );
      return;
    } finally {
      onAfterCameraLaunch?.();
    }
    if (result.didCancel || result.errorCode) return;

    const asset = result.assets?.[0];
    if (!asset?.uri) return;

    if (reviewBeforeUpload) {
      setPendingPhoto(asset);
      return;
    }
    await uploadPhoto(asset);
  }, [t, onBeforeCameraLaunch, onAfterCameraLaunch, reviewBeforeUpload, uploadPhoto]);

  const confirmPendingPhoto = useCallback(async () => {
    if (!pendingPhoto) return;
    const uploaded = await uploadPhoto(pendingPhoto);
    if (uploaded) setPendingPhoto(null);
  }, [pendingPhoto, uploadPhoto]);

  const retakePendingPhoto = useCallback(() => {
    setPendingPhoto(null);
    handleCameraUpload();
  }, [handleCameraUpload]);

  const discardPendingPhoto = useCallback(() => setPendingPhoto(null), []);

  const handleExternalDeviceUpload = useCallback(() => {
    setIsUploadModalVisible(false);
    navigation.navigate('DocumentUploadQRPage', { pathwayId });
  }, [navigation, pathwayId]);

  return {
    isUploadModalVisible,
    setIsUploadModalVisible,
    isUploading,
    handleCameraUpload,
    handleExternalDeviceUpload,
    pendingPhoto,
    confirmPendingPhoto,
    retakePendingPhoto,
    discardPendingPhoto,
  };
};
